import React, { useState, useEffect } from "react";
import { Col } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  PenTool,
  Code,
  TrendingUp,
  Palette,
  Wallet,
  GraduationCap,
  Heart,
  Scale,
  Cpu,
  Shield,
  Zap,
  Users,
  Star,
} from "lucide-react";
import { SpaceBackground } from "@components";

// Browser & platform icons (PNG from uxwing.com)
import chromeLogo from "@assets/images/browser/chrome.png";
import firefoxLogo from "@assets/images/browser/firefox.png";
import safariLogo from "@assets/images/browser/safari.png";
import edgeLogo from "@assets/images/browser/edge.png";
import braveLogo from "@assets/images/browser/brave.png";
import appleLogo from "@assets/images/browser/apple.png";
import androidLogo from "@assets/images/browser/android.png";

const LeftContent = () => {
  const [activeCopilot, setActiveCopilot] = useState(0);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const copilots = [
    { icon: Scale, name: "Legal Copilot", color: "#94a3b8" },
    { icon: Heart, name: "Wellness Copilot", color: "#f87171" },
    { icon: Wallet, name: "Finance Copilot", color: "#34d399" },
    { icon: Code, name: "Code Copilot", color: "#60a5fa" },
    { icon: TrendingUp, name: "Marketing Copilot", color: "#f472b6" },
    { icon: PenTool, name: "Writing Copilot", color: "#fb923c" },
    { icon: GraduationCap, name: "Learning Copilot", color: "#fbbf24" },
    { icon: Palette, name: "Design Copilot", color: "#a78bfa" },
  ];

  const features = [
    { icon: Cpu, title: "10+ AI Models", desc: "GPT, Claude, Gemini & more", color: "#8b5cf6" },
    { icon: Zap, title: "Lightning Fast", desc: "Instant responses", color: "#f59e0b" },
    { icon: Shield, title: "Privacy First", desc: "Your data stays yours", color: "#10b981" },
    { icon: Users, title: "Team Ready", desc: "Collaborate seamlessly", color: "#3b82f6" },
  ];

  const browsers = [
    { logo: chromeLogo, name: "Chrome" },
    { logo: firefoxLogo, name: "Firefox" },
    { logo: safariLogo, name: "Safari" },
    { logo: edgeLogo, name: "Edge" },
    { logo: braveLogo, name: "Brave" },
  ];

  const testimonials = [
    { quote: "Legal Copilot helped me understand complex contract terms before my meeting. Super helpful research tool.", author: "Sarah Chen", role: "Startup Founder" },
    { quote: "I use Finance Copilot to learn about budgeting and investing concepts. Great for building financial literacy.", author: "Michael Rodriguez", role: "Freelance Designer" },
    { quote: "Marketing Copilot brainstorms campaign ideas with me 24/7. My go-to creative thinking partner.", author: "Emily Watson", role: "Small Business Owner" },
    { quote: "Code Copilot helps me understand error messages and debug faster. Saved me hours of Stack Overflow.", author: "James Park", role: "Software Developer" },
  ];

  const stats = [
    { value: "50K+", label: "Users" },
    { value: "10M+", label: "Tasks" },
    { value: "99.9%", label: "Uptime" },
    { value: "4.9", label: "Rating", icon: Star },
  ];

  useEffect(() => {
    const copilotInterval = setInterval(() => {
      setActiveCopilot((prev) => (prev + 1) % copilots.length);
    }, 2000);
    const testimonialInterval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => {
      clearInterval(copilotInterval);
      clearInterval(testimonialInterval);
    };
  }, []);

  return (
    <Col
      lg={16}
      md={0}
      sm={0}
      xs={0}
      style={{
        height: "100vh",
        background: "linear-gradient(135deg, #0a0612 0%, #1a0a2e 50%, #0a0612 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <SpaceBackground />

      {/* Ambient glow effects */}
      <div style={{
        position: "absolute",
        top: "20%",
        left: "30%",
        width: "300px",
        height: "300px",
        background: "radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 70%)",
        filter: "blur(60px)",
        pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute",
        bottom: "30%",
        right: "20%",
        width: "250px",
        height: "250px",
        background: "radial-gradient(circle, rgba(236,72,153,0.1) 0%, transparent 70%)",
        filter: "blur(60px)",
        pointerEvents: "none",
      }} />

      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          zIndex: 1,
          padding: "28px 40px",
          gap: "20px",
        }}
      >
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: "center" }}
        >
          <h1
            style={{
              color: "#fff",
              fontSize: "38px",
              fontWeight: 700,
              lineHeight: 1.1,
              margin: "0 0 8px 0",
              letterSpacing: "-0.5px",
            }}
          >
            Your Personal{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #a78bfa 0%, #ec4899 50%, #f97316 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundSize: "200% 200%",
                animation: "gradientShift 3s ease infinite",
              }}
            >
              AI Copilot
            </span>
          </h1>
          <p
            style={{
              color: "rgba(255,255,255,0.6)",
              fontSize: "15px",
              margin: "0 auto",
              maxWidth: "420px",
            }}
          >
            AI assistance for legal, finance, health, marketing, code & more — all in one place
          </p>
        </motion.div>

        {/* AI Copilots - Compact Pill Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div style={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            flexWrap: "wrap",
            maxWidth: "560px",
          }}>
            {copilots.map((copilot, idx) => {
              const Icon = copilot.icon;
              const isActive = idx === activeCopilot;
              return (
                <motion.div
                  key={idx}
                  animate={{
                    scale: isActive ? 1.08 : 1,
                    y: isActive ? -2 : 0,
                  }}
                  transition={{ duration: 0.2 }}
                  onMouseEnter={() => setActiveCopilot(idx)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "6px 12px",
                    background: isActive
                      ? `linear-gradient(135deg, ${copilot.color}30 0%, ${copilot.color}10 100%)`
                      : "rgba(255,255,255,0.03)",
                    borderRadius: "18px",
                    border: isActive
                      ? `1px solid ${copilot.color}60`
                      : "1px solid rgba(255,255,255,0.06)",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <Icon
                    size={14}
                    color={isActive ? copilot.color : "rgba(255,255,255,0.5)"}
                  />
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: isActive ? copilot.color : "rgba(255,255,255,0.5)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {copilot.name}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Features - Compact Icons with Labels */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{ display: "flex", justifyContent: "center", gap: "20px" }}
        >
          {features.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={idx}
                whileHover={{ scale: 1.05, y: -2 }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "12px",
                    background: `linear-gradient(135deg, ${feature.color}20 0%, ${feature.color}05 100%)`,
                    border: `1px solid ${feature.color}30`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 4px 12px ${feature.color}15`,
                  }}
                >
                  <Icon size={20} color={feature.color} />
                </div>
                <span style={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "rgba(255,255,255,0.6)",
                  textAlign: "center",
                  maxWidth: "70px",
                  lineHeight: 1.2,
                }}>
                  {feature.title}
                </span>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Platforms Section - Compact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{ textAlign: "center" }}
        >
          <p style={{
            color: "rgba(255,255,255,0.4)",
            fontSize: "10px",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "1.5px",
            margin: "0 0 10px 0",
          }}>
            Available Everywhere
          </p>

          <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginBottom: "12px" }}>
            {browsers.map((browser, idx) => (
              <motion.div
                key={idx}
                whileHover={{ scale: 1.15, y: -2 }}
                style={{
                  width: "38px",
                  height: "38px",
                  borderRadius: "10px",
                  background: "rgba(255,255,255,0.95)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
                title={browser.name}
              >
                <img src={browser.logo} alt={browser.name} style={{ width: 22, height: 22 }} />
              </motion.div>
            ))}
          </div>

          {/* App Store Buttons */}
          <div style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 14px",
                background: "rgba(0,0,0,0.6)",
                borderRadius: "8px",
                cursor: "pointer",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <img src={appleLogo} alt="Apple" style={{ width: 14, height: 14, filter: "brightness(0) invert(1)" }} />
              <span style={{ fontSize: "12px", fontWeight: 600, color: "#fff" }}>App Store</span>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 14px",
                background: "rgba(0,0,0,0.6)",
                borderRadius: "8px",
                cursor: "pointer",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <img src={androidLogo} alt="Android" style={{ width: 14, height: 14 }} />
              <span style={{ fontSize: "12px", fontWeight: 600, color: "#fff" }}>Google Play</span>
            </motion.div>
          </div>
        </motion.div>

        {/* Testimonial Glass Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          style={{
            background: "rgba(255,255,255,0.03)",
            borderRadius: "16px",
            padding: "18px 24px",
            border: "1px solid rgba(255,255,255,0.08)",
            maxWidth: "440px",
            backdropFilter: "blur(20px)",
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTestimonial}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              style={{ textAlign: "center" }}
            >
              <div style={{ display: "flex", justifyContent: "center", gap: "3px", marginBottom: "8px" }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} size={14} fill="#fbbf24" color="#fbbf24" />
                ))}
              </div>
              <p style={{
                color: "#fff",
                fontSize: "14px",
                fontWeight: 500,
                margin: "0 0 8px 0",
                fontStyle: "italic",
                lineHeight: 1.5,
              }}>
                "{testimonials[activeTestimonial].quote}"
              </p>
              <p style={{
                color: "rgba(255,255,255,0.7)",
                fontSize: "13px",
                margin: 0,
                fontWeight: 500,
              }}>
                {testimonials[activeTestimonial].author}
              </p>
              <p style={{
                color: "rgba(255,255,255,0.4)",
                fontSize: "11px",
                margin: "2px 0 0 0",
              }}>
                {testimonials[activeTestimonial].role}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Progress dots */}
          <div style={{ display: "flex", justifyContent: "center", gap: "5px", marginTop: "12px" }}>
            {testimonials.map((_, idx) => (
              <div
                key={idx}
                onClick={() => setActiveTestimonial(idx)}
                style={{
                  width: idx === activeTestimonial ? "16px" : "5px",
                  height: "5px",
                  borderRadius: "3px",
                  background: idx === activeTestimonial ? "#a78bfa" : "rgba(255,255,255,0.2)",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
              />
            ))}
          </div>
        </motion.div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "36px",
          }}
        >
          {stats.map((stat, idx) => (
            <div key={idx} style={{ textAlign: "center" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
              }}>
                <span style={{
                  fontSize: "22px",
                  fontWeight: 700,
                  color: "#fff",
                  lineHeight: 1,
                }}>
                  {stat.value}
                </span>
                {stat.icon && <stat.icon size={14} fill="#fbbf24" color="#fbbf24" />}
              </div>
              <div style={{
                fontSize: "11px",
                color: "rgba(255,255,255,0.5)",
                fontWeight: 500,
                marginTop: "3px",
              }}>
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <Sparkles size={11} color="#a78bfa" />
          <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "9px" }}>
            Powered by GPT, Claude, Gemini & more
          </span>
        </motion.div>
      </div>

      {/* CSS Animation for gradient */}
      <style>{`
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </Col>
  );
};

export default LeftContent;
